import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";

const FabDialog = ({ fab, onClose, onSaved }) => {
  const isNew = !fab.id;
  const [f, setF] = useState({ name: fab.name || "", code: fab.code || "", gstin: fab.gstin || "", location: fab.location || "", phone: fab.phone || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isNew) api.get("/fabricators/next-code").then((r) => setF((x) => (x.code ? x : { ...x, code: r.data.code })));
  }, [isNew]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) await api.post("/fabricators", f);
      else await api.put(`/fabricators/${fab.id}`, f);
      toast.success(isNew ? "Fabricator added" : "Fabricator updated");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const input = (k, label, extra = {}) => (
    <div><label className="label">{label}</label><input className="field" value={f[k]} onChange={set(k)} data-testid={`fabricator-${k}-input`} {...extra} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto sm:max-w-md" data-testid="fabricator-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isNew ? "Add fabricator" : "Edit fabricator"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {input("name", "Fabricator name *", { required: true })}
          <div className="grid grid-cols-2 gap-3">{input("code", "Code *", { required: true })}{input("phone", "Phone", { type: "tel" })}</div>
          {input("gstin", "GSTIN", { maxLength: 15 })}
          {input("location", "Location")}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn btn-outline" onClick={onClose} data-testid="fabricator-cancel-btn">Cancel</button>
            <button className="btn btn-primary" disabled={busy} data-testid="fabricator-save-btn">{busy && <Loader2 size={18} className="animate-spin" />} Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const FabricatorsTab = () => {
  const { isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => {
    api.get("/fabricators").then((r) => setList(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const remove = async () => {
    try {
      await api.delete(`/fabricators/${deleting.id}`);
      toast.success("Fabricator deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDeleting(null);
  };
  return (
    <div className="space-y-3" data-testid="fabricators-tab">
      <button className="btn btn-primary w-full" onClick={() => setEditing({})} data-testid="add-fabricator-btn"><Plus size={18} /> Add fabricator</button>
      {!list.length && <p className="panel p-6 text-center text-muted-foreground">No fabricators yet.</p>}
      {list.map((f) => (
        <div key={f.id} className="panel flex items-start gap-3 p-4" data-testid={`fabricator-row-${f.code}`}>
          <div className="min-w-0 flex-1">
            <span className="mono text-sm font-bold">{f.code}</span>
            <p className="font-semibold">{f.name}</p>
            <p className="break-words text-xs text-muted-foreground">{[f.gstin && `GSTIN ${f.gstin}`, f.location, f.phone].filter(Boolean).join(" · ")}</p>
          </div>
          <button className="icon-btn" onClick={() => setEditing(f)} aria-label="Edit fabricator" data-testid={`fabricator-edit-${f.code}`}><Pencil size={18} /></button>
          {isAdmin && <button className="icon-btn hover:!text-destructive" onClick={() => setDeleting(f)} aria-label="Delete fabricator" data-testid={`fabricator-delete-${f.code}`}><Trash2 size={18} /></button>}
        </div>
      ))}
      {editing && <FabDialog fab={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ConfirmDialog open={!!deleting} onCancel={() => setDeleting(null)} onConfirm={remove} title={`Delete ${deleting?.name}?`} body="Existing job orders keep their copy of the fabricator's details." />
    </div>
  );
};
